import React, { useState, useEffect } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { Eye, EyeOff, Shield, Lock } from 'lucide-react'
import { useAuth } from '../../contexts/AuthContext'
import { COMPANY_NAME } from '../../constants/contact'
import { useLocalization } from '../../contexts/LocalizationContext'
import { Logo } from '../Logo'

interface AuthFormProps {
  mode: 'signin' | 'signup'
  onToggleMode: () => void
}

export function AuthForm({ mode, onToggleMode }: AuthFormProps) {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [fullName, setFullName] = useState('')
  const [src, setSrc] = useState('')
  const [eventId, setEventId] = useState('')
  const [error, setError] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})
  const [isTransitioning, setIsTransitioning] = useState(false)

  const { signIn, signUp, signInWithGoogle } = useAuth()
  const { t } = useLocalization()
  const [searchParams] = useSearchParams()
  const isLogin = mode === 'signin'

  // Capture les paramètres depuis l'URL
  useEffect(() => {
    const srcParam = searchParams.get('src')
    if (srcParam) {
      setSrc(srcParam)
    }

    const eventIdParam = searchParams.get('event_id')
    if (eventIdParam) {
      setEventId(eventIdParam)
    }

    // Payment provider override (temporaire)
    // Important: on NE veut PAS que Stripe devienne le défaut pour toujours.
    // Donc on utilise sessionStorage (onglet courant) plutôt que localStorage.
    const paymentParam = searchParams.get('payment')
    if (paymentParam === 'stripe' || paymentParam === 'systempay') {
      sessionStorage.setItem('preferred_payment_provider', paymentParam)
    }

    // Préserver le mode direct entre signup -> signin (ex: confirmation email)
    const subParam = searchParams.get('sub')
    const modeParam = searchParams.get('mode')
    const requestedSubscriptionMode = subParam || modeParam
    if (requestedSubscriptionMode === 'direct' || requestedSubscriptionMode === 'direct2') {
      sessionStorage.setItem('preferred_subscription_mode', requestedSubscriptionMode)
      localStorage.setItem('preferred_subscription_mode', requestedSubscriptionMode)
    }
  }, [searchParams])


  const validateField = (field: string, value: string) => {
    const errors = { ...fieldErrors }
    switch (field) {
      case 'email':
        if (value && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value))
          errors.email = t('auth.invalidEmail')
        else delete errors.email
        break
      case 'password':
        if (value && value.length < 6)
          errors.password = t('auth.passwordTooShort')
        else delete errors.password
        break
      case 'fullName':
        if (value && value.trim().length < 2)
          errors.fullName = t('auth.nameTooShort')
        else delete errors.fullName
        break
    }
    setFieldErrors(errors)
  }

  const getPasswordStrength = (pw: string) => {
    let score = 0
    if (pw.length >= 6) score++
    if (pw.length >= 10) score++
    if (/[A-Z]/.test(pw) && /[a-z]/.test(pw)) score++
    if (/\d/.test(pw)) score++
    if (/[^A-Za-z0-9]/.test(pw)) score++

    if (score <= 1) return { level: 1, label: t('auth.passwordWeak'), color: 'bg-red-400' }
    if (score <= 2) return { level: 2, label: t('auth.passwordFair'), color: 'bg-yellow-400' }
    if (score <= 3) return { level: 3, label: t('auth.passwordGood'), color: 'bg-blue-400' }
    return { level: 4, label: t('auth.passwordStrong'), color: 'bg-green-500' }
  }

  const handleToggle = () => {
    setIsTransitioning(true)
    setTimeout(() => {
      onToggleMode()
      setIsTransitioning(false)
    }, 150)
  }

  const handleGoogleSignIn = async () => {
    setError('')
    // Persister src/eventId avant la redirection OAuth (on perd les params URL)
    if (src) sessionStorage.setItem('oauth_src', src)
    if (eventId) sessionStorage.setItem('oauth_event_id', eventId)
    try {
      await signInWithGoogle()
    } catch (error: any) {
      setError(error.message || 'An error occurred')
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setIsLoading(true)

    try {
      if (mode === 'signin') {
        await signIn(email, password)
      } else {
        await signUp(email, password, fullName, src, eventId)
      }
    } catch (error: any) {
      setError(error.message || 'An error occurred')
    } finally {
      setIsLoading(false)
    }
  }

  const strength = getPasswordStrength(password)

  return (
    <div className="min-h-screen bg-paybyphone-light flex flex-col">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className={`flex items-center ${mode === 'signup' ? 'hidden md:flex' : ''}`}>
              <Logo width={150} height={36} />
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="flex-1 flex items-center justify-center px-4 py-6 sm:py-12">
        <div className="max-w-md w-full">
          <div className={`card p-4 sm:p-6 transition-opacity duration-150 ${isTransitioning ? 'opacity-0' : 'opacity-100'}`}>
            <div className="text-center mb-5 sm:mb-8">
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">
                {mode === 'signin' ? t('auth.welcomeBack') : t('auth.createYourAccount')}
              </h2>
              <p className="text-gray-600">
                {mode === 'signin'
                  ? t('auth.signInDescription')
                  : t('auth.signUpDescription')
                }
              </p>
            </div>

            {/* Google Sign In */}
            <button
              type="button"
              onClick={handleGoogleSignIn}
              className="w-full flex items-center justify-center gap-3 min-h-[48px] px-4 py-3 border border-gray-300 rounded-lg bg-white hover:bg-gray-50 transition-colors duration-200 font-medium text-gray-700"
            >
              <svg width="20" height="20" viewBox="0 0 24 24">
                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
              </svg>
              {t('auth.continueWithGoogle')}
            </button>

            {/* Separator */}
            <div className="relative my-4 sm:my-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-3 bg-white text-gray-500">{t('auth.orContinueWith')}</span>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
            {mode === 'signup' && (
              <div>
                <label htmlFor="fullName" className="block text-sm font-medium text-gray-700 mb-2">
                  {t('auth.fullName')}
                </label>
                <input
                  id="fullName"
                  type="text"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  onBlur={() => validateField('fullName', fullName)}
                  className={`input-field ${fieldErrors.fullName ? 'border-red-400 focus:ring-red-400' : ''}`}
                  placeholder={t('auth.enterFullName')}
                  required
                  autoComplete="name"
                  enterKeyHint="next"
                  autoCapitalize="words"
                />
                {fieldErrors.fullName && (
                  <p className="text-red-500 text-xs mt-1">{fieldErrors.fullName}</p>
                )}
              </div>
            )}

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                {t('auth.emailAddress')}
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onBlur={() => validateField('email', email)}
                className={`input-field ${fieldErrors.email ? 'border-red-400 focus:ring-red-400' : ''}`}
                placeholder={t('auth.enterEmail')}
                required
                autoComplete="email"
                inputMode="email"
                enterKeyHint="next"
                autoCapitalize="off"
                autoCorrect="off"
              />
              {fieldErrors.email && (
                <p className="text-red-500 text-xs mt-1">{fieldErrors.email}</p>
              )}
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                {t('auth.password')}
              </label>
              <div className="relative">
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  onBlur={() => validateField('password', password)}
                  className={`input-field pr-12 ${fieldErrors.password ? 'border-red-400 focus:ring-red-400' : ''}`}
                  placeholder={t('auth.enterPassword')}
                  required
                  minLength={6}
                  autoComplete={isLogin ? 'current-password' : 'new-password'}
                  enterKeyHint={isLogin ? 'done' : 'go'}
                  autoCapitalize="off"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 p-1"
                  tabIndex={-1}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
              {fieldErrors.password && (
                <p className="text-red-500 text-xs mt-1">{fieldErrors.password}</p>
              )}
              {!isLogin && password && (
                <div className="mt-2">
                  <div className="flex gap-1 h-1">
                    {[1, 2, 3, 4].map(i => (
                      <div key={i} className={`flex-1 rounded-full transition-colors duration-300 ${
                        i <= strength.level ? strength.color : 'bg-gray-200'
                      }`} />
                    ))}
                  </div>
                  <p className="text-xs text-gray-500 mt-1">{strength.label}</p>
                </div>
              )}
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 animate-shake">
                <p className="text-red-600 text-sm">{error}</p>
              </div>
            )}

            <button
              type="submit"
              disabled={isLoading}
              className="btn-primary w-full min-h-[48px] disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? (
                <div className="flex items-center justify-center">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                  {mode === 'signin' ? t('auth.signingIn') : t('auth.creatingAccount')}
                </div>
              ) : (
                mode === 'signin' ? t('auth.signIn') : t('auth.createAccount')
              )}
            </button>

            {mode === 'signup' && (
              <div className="flex items-center justify-center gap-4 text-xs text-gray-400 mt-4">
                <span className="flex items-center gap-1">
                  <Shield size={14} /> {t('auth.secureSignup')}
                </span>
                <span className="flex items-center gap-1">
                  <Lock size={14} /> {t('auth.encryptedData')}
                </span>
              </div>
            )}
          </form>

            <div className="mt-6 text-center">
              <p className="text-gray-600">
                {mode === 'signin' ? t('auth.noAccount') : t('auth.hasAccount')}
                <button
                  onClick={handleToggle}
                  className="ml-2 text-paybyphone-green font-medium hover:underline"
                >
                  {mode === 'signin' ? t('auth.signUp') : t('auth.signIn')}
                </button>
              </p>
            </div>
          </div>

          {/* Footer with legal links */}
          <div className="mt-8 text-center">
            <div className="flex justify-center flex-wrap gap-2 sm:gap-6 text-sm">
              <Link
                to="/terms"
                className="text-gray-500 hover:text-paybyphone-green transition-colors py-2 px-1"
              >
                {t('auth.termsOfService')}
              </Link>
              <Link
                to="/privacy"
                className="text-gray-500 hover:text-paybyphone-green transition-colors py-2 px-1"
              >
                {t('auth.privacyPolicy')}
              </Link>
              <Link
                to="/contact"
                className="text-gray-500 hover:text-paybyphone-green transition-colors py-2 px-1"
              >
                {t('auth.support')}
              </Link>
            </div>
            <p className="text-xs text-gray-400 mt-4">
              © 2026 {COMPANY_NAME}. {t('auth.allRightsReserved')}
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}
